import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  container: {
    overflowX: 'auto',
    position: 'relative',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '240px',
  },
  clusterCardsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginTop: '16px',
  },
  clusterCard: {
    flex: '1 1 200px',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '12px 16px',
    background: '#fafbfc',
    minWidth: '180px',
  },
  clusterCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  clusterDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  clusterLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#1f2937',
  },
  clusterStates: {
    fontSize: '12px',
    color: '#6b7280',
    marginBottom: '6px',
    lineHeight: '1.4',
  },
  clusterStats: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '11px',
    color: '#374151',
  },
  clusterStatLabel: {
    color: '#9ca3af',
  },
  clusterStatValue: {
    fontWeight: 600,
  },
  emptyState: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
  },
};

/**
 * Count total leaf nodes under a d3 hierarchy node.
 */
function countLeaves(node) {
  if (!node.children || node.children.length === 0) return 1;
  return node.children.reduce((sum, c) => sum + countLeaves(c), 0);
}

/**
 * Build a lookup from state name to cluster info.
 */
function buildStateClusterMap(clusters) {
  const map = {};
  if (!clusters) return map;
  clusters.forEach((cluster) => {
    (cluster.states || []).forEach((state) => {
      map[state] = cluster;
    });
  });
  return map;
}

export default function GeographicClusterMap({ data }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const stateClusterMap = useMemo(() => {
    return data && data.clusters ? buildStateClusterMap(data.clusters) : {};
  }, [data]);

  const leafCount = useMemo(() => {
    if (!data || !data.tree) return 0;
    return countLeaves(data.tree);
  }, [data]);

  useEffect(() => {
    if (!data || !data.tree) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const tree = data.tree;
    const clusters = data.clusters || [];

    // Dimensions
    const margin = { top: 40, right: 160, bottom: 20, left: 20 };
    const rowHeight = 25;
    const leaves = countLeaves(tree);
    const height = margin.top + leaves * rowHeight + margin.bottom;
    const width = 700;

    svg
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('width', '100%')
      .attr('height', '100%')
      .style('max-width', `${width}px`);

    // Build d3 hierarchy
    const root = d3.hierarchy(tree);

    // Cluster layout (dendrogram) -- horizontal orientation
    const clusterLayout = d3.cluster().size([
      height - margin.top - margin.bottom,
      width - margin.left - margin.right,
    ]);
    clusterLayout(root);

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    // Distance scale along the top
    const maxDistance = root.data.distance != null ? root.data.distance : 1;
    const distanceScale = d3
      .scaleLinear()
      .domain([0, maxDistance])
      .range([width - margin.left - margin.right, 0]);

    // Map each node's x position (horizontal) based on its distance
    // Leaves get x = width - margin.left - margin.right (rightmost)
    // Internal nodes get x based on their distance value
    function nodeX(d) {
      if (!d.children || d.children.length === 0) {
        // Leaf node: rightmost position
        return width - margin.left - margin.right;
      }
      const dist = d.data.distance != null ? d.data.distance : 0;
      return distanceScale(dist);
    }

    // Use the cluster layout y positions (vertical spacing)
    function nodeY(d) {
      return d.x; // d.x from cluster layout is the vertical position
    }

    // Distance axis at the top
    const axisScale = d3
      .scaleLinear()
      .domain([maxDistance, 0])
      .range([0, width - margin.left - margin.right]);

    const axis = d3.axisTop(axisScale).ticks(6).tickSize(4);

    g.append('g')
      .attr('transform', 'translate(0, -10)')
      .call(axis)
      .call((g) => {
        g.select('.domain').attr('stroke', '#d1d5db');
        g.selectAll('.tick line').attr('stroke', '#d1d5db');
        g.selectAll('.tick text')
          .attr('fill', '#9ca3af')
          .attr('font-size', '10px');
      });

    g.append('text')
      .attr('x', (width - margin.left - margin.right) / 2)
      .attr('y', -28)
      .attr('text-anchor', 'middle')
      .attr('fill', '#9ca3af')
      .attr('font-size', '10px')
      .text('Merge Distance');

    // Build a state-to-cluster map for coloring
    const stateColorMap = {};
    clusters.forEach((cluster) => {
      (cluster.states || []).forEach((state) => {
        stateColorMap[state] = cluster.color || '#6b7280';
      });
    });

    // Helper: get the color for a node based on its descendants' cluster
    function getNodeColor(d) {
      if (!d.children || d.children.length === 0) {
        // Leaf: use its cluster color
        return stateColorMap[d.data.name] || '#6b7280';
      }
      // Internal: check if all descendants are in the same cluster
      const leafNames = d.leaves().map((l) => l.data.name);
      const colors = new Set(leafNames.map((n) => stateColorMap[n] || '#6b7280'));
      if (colors.size === 1) {
        return [...colors][0];
      }
      return '#9ca3af'; // mixed clusters
    }

    // Draw elbow links
    g.selectAll('.link')
      .data(root.links())
      .enter()
      .append('path')
      .attr('class', 'link')
      .attr('d', (d) => {
        const sx = nodeX(d.source);
        const sy = nodeY(d.source);
        const tx = nodeX(d.target);
        const ty = nodeY(d.target);
        // Elbow/step connection: horizontal from source, then vertical, then horizontal to target
        return `M${sx},${sy}H${sx}V${ty}H${tx}`;
      })
      .attr('fill', 'none')
      .attr('stroke', (d) => getNodeColor(d.source))
      .attr('stroke-width', 1.5)
      .attr('stroke-opacity', 0.6);

    // Draw internal nodes
    const internalNodes = root.descendants().filter((d) => d.children && d.children.length > 0);

    g.selectAll('.internal-node')
      .data(internalNodes)
      .enter()
      .append('circle')
      .attr('class', 'internal-node')
      .attr('cx', (d) => nodeX(d))
      .attr('cy', (d) => nodeY(d))
      .attr('r', 4)
      .attr('fill', (d) => getNodeColor(d))
      .attr('stroke', '#fff')
      .attr('stroke-width', 1.5)
      .style('cursor', 'pointer')
      .on('mouseenter', function (event, d) {
        d3.select(this).attr('r', 6).attr('stroke-width', 2);
        const subtreeLeaves = d.leaves().length;
        const dist = d.data.distance != null ? d.data.distance : 0;
        setTooltip({
          x: event.clientX + 14,
          y: event.clientY - 14,
          type: 'internal',
          distance: dist,
          subtreeCount: subtreeLeaves,
          states: d.leaves().map((l) => l.data.name),
        });
      })
      .on('mousemove', function (event) {
        setTooltip((prev) => prev ? { ...prev, x: event.clientX + 14, y: event.clientY - 14 } : prev);
      })
      .on('mouseleave', function () {
        d3.select(this).attr('r', 4).attr('stroke-width', 1.5);
        setTooltip(null);
      });

    // Draw leaf nodes
    const leafNodes = root.leaves();

    g.selectAll('.leaf-node')
      .data(leafNodes)
      .enter()
      .append('circle')
      .attr('class', 'leaf-node')
      .attr('cx', (d) => nodeX(d))
      .attr('cy', (d) => nodeY(d))
      .attr('r', 5)
      .attr('fill', (d) => stateColorMap[d.data.name] || '#6b7280')
      .attr('stroke', '#fff')
      .attr('stroke-width', 1.5)
      .style('cursor', 'pointer')
      .on('mouseenter', function (event, d) {
        d3.select(this).attr('r', 7).attr('stroke-width', 2);
        const cluster = stateClusterMap[d.data.name];
        setTooltip({
          x: event.clientX + 14,
          y: event.clientY - 14,
          type: 'leaf',
          state: d.data.name,
          avgPrice: d.data.avgPrice != null ? d.data.avgPrice : (cluster ? cluster.avgPrice : null),
          clusterLabel: cluster ? `Cluster ${cluster.id}` : 'Unassigned',
          clusterColor: cluster ? cluster.color : '#6b7280',
        });
      })
      .on('mousemove', function (event) {
        setTooltip((prev) => prev ? { ...prev, x: event.clientX + 14, y: event.clientY - 14 } : prev);
      })
      .on('mouseleave', function () {
        d3.select(this).attr('r', 5).attr('stroke-width', 1.5);
        setTooltip(null);
      });

    // Leaf labels: state abbreviation + avg price
    g.selectAll('.leaf-label')
      .data(leafNodes)
      .enter()
      .append('text')
      .attr('class', 'leaf-label')
      .attr('x', (d) => nodeX(d) + 10)
      .attr('y', (d) => nodeY(d))
      .attr('dominant-baseline', 'central')
      .attr('font-size', '11px')
      .attr('fill', '#374151')
      .attr('font-weight', 500)
      .text((d) => {
        const name = d.data.name || '';
        const cluster = stateClusterMap[name];
        const price = d.data.avgPrice != null ? d.data.avgPrice : (cluster ? cluster.avgPrice : null);
        if (price != null) {
          return `${name}  $${price.toFixed(2)}`;
        }
        return name;
      });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data, stateClusterMap]);

  // Empty state
  if (!data || !data.tree) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Pricing Similarity Clusters</div>
        <div style={styles.subtitle}>
          States grouped by pricing pattern similarity (hierarchical clustering)
        </div>
        <div style={styles.emptyState}>No data available</div>
      </div>
    );
  }

  const clusters = data.clusters || [];
  const svgHeight = Math.max(200, 40 + leafCount * 25 + 20);

  return (
    <div style={styles.card}>
      <div style={styles.title}>Pricing Similarity Clusters</div>
      <div style={styles.subtitle}>
        States grouped by pricing pattern similarity (hierarchical clustering)
      </div>

      <div style={styles.container}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && tooltip.type === 'leaf' && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.state}
          </div>
          {tooltip.avgPrice != null && (
            <div style={{ color: '#2563eb', fontWeight: 600, marginBottom: '4px' }}>
              Avg price: ${tooltip.avgPrice.toFixed(2)}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span
              style={{
                ...styles.clusterDot,
                background: tooltip.clusterColor,
                display: 'inline-block',
              }}
            />
            <span style={{ color: '#6b7280' }}>{tooltip.clusterLabel}</span>
          </div>
        </div>
      )}

      {tooltip && tooltip.type === 'internal' && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            Internal Node
          </div>
          <div style={{ color: '#6b7280', marginBottom: '2px' }}>
            Merge distance: {tooltip.distance.toFixed(4)}
          </div>
          <div style={{ color: '#6b7280', marginBottom: '4px' }}>
            States in subtree: {tooltip.subtreeCount}
          </div>
          {tooltip.states.length <= 8 && (
            <div style={{ fontSize: '11px', color: '#374151' }}>
              {tooltip.states.join(', ')}
            </div>
          )}
        </div>
      )}

      {/* Cluster summary cards */}
      {clusters.length > 0 && (
        <div style={styles.clusterCardsContainer}>
          {clusters.map((cluster) => {
            const states = cluster.states || [];
            const prices = states
              .map((s) => {
                const info = stateClusterMap[s];
                return info && info.avgPrice != null ? info.avgPrice : null;
              })
              .filter((p) => p != null);
            const minPrice = prices.length > 0 ? Math.min(...prices) : null;
            const maxPrice = prices.length > 0 ? Math.max(...prices) : null;

            return (
              <div key={cluster.id} style={styles.clusterCard}>
                <div style={styles.clusterCardHeader}>
                  <span style={{ ...styles.clusterDot, background: cluster.color || '#6b7280' }} />
                  <span style={styles.clusterLabel}>Cluster {cluster.id}</span>
                  <span style={{ fontSize: '11px', color: '#9ca3af', marginLeft: 'auto' }}>
                    {states.length} state{states.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div style={styles.clusterStates}>
                  {states.join(', ')}
                </div>
                <div style={styles.clusterStats}>
                  <div>
                    <span style={styles.clusterStatLabel}>Avg: </span>
                    <span style={styles.clusterStatValue}>
                      ${cluster.avgPrice != null ? cluster.avgPrice.toFixed(2) : '--'}
                    </span>
                  </div>
                  {minPrice != null && maxPrice != null && (
                    <div>
                      <span style={styles.clusterStatLabel}>Range: </span>
                      <span style={styles.clusterStatValue}>
                        ${minPrice.toFixed(2)} - ${maxPrice.toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
